import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AppLayout } from "../components/tabon/AppLayout";
import { useAuth } from "../contexts/AuthContext";
import { api, formatError } from "../lib/api";
import { ChevronLeft, Search, Save } from "lucide-react";

// Menu → Tax groups: a searchable table of every item with a tax-group selector and one Save — how a
// bar tags a hundred drinks to an 18% alcohol group without opening each item (Prompt Z, Commit 4).
export default function ItemTaxGroups() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [groups, setGroups] = useState({});   // item_id -> tax_group
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // The venue's tax groups (Settings → Billing & Tax), or a food fallback until the migration runs.
  const taxGroups = (Array.isArray(user?.venue?.tax_table) && user.venue.tax_table.length)
    ? user.venue.tax_table
    : [{ group: "food", label: "Food & non-alcoholic", rate: user?.venue?.gst_rate ?? 0 }];
  const groupKeys = taxGroups.map((g) => g.group);

  const load = async () => {
    try {
      const [i, c] = await Promise.all([api.get("/menu/items"), api.get("/menu/categories")]);
      const its = i.data || [];
      setItems(its);
      setCats(c.data || []);
      setGroups(Object.fromEntries(its.map((it) => [it.id, it.tax_group || "food"])));
    } catch (err) { toast.error(formatError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const catName = (id) => (cats.find((c) => c.id === id) || {}).name || "—";
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) =>
      (it.name || "").toLowerCase().includes(q) ||
      catName(it.category_id).toLowerCase().includes(q));
  }, [items, search, cats]);

  const dirty = useMemo(
    () => items.some((it) => (groups[it.id] || "food") !== (it.tax_group || "food")),
    [items, groups]);

  // Bulk: set every item currently in view to one group — the bar-tags-100-drinks path.
  const setAllVisible = (g) => setGroups((p) => {
    const n = { ...p };
    visible.forEach((it) => { n[it.id] = g; });
    return n;
  });

  const save = async () => {
    const changed = items
      .filter((it) => (groups[it.id] || "food") !== (it.tax_group || "food"))
      .map((it) => ({ item_id: it.id, tax_group: groups[it.id] || "food" }));
    if (!changed.length) { toast.info("No changes to save"); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/menu/item-tax-groups", { groups: changed });
      toast.success(`Saved ${data.updated} item${data.updated === 1 ? "" : "s"}`);
      await load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setSaving(false); }
  };

  return (
    <AppLayout>
      <div className="max-w-[820px] mx-auto px-4 md:px-6 py-6">
        <button onClick={() => navigate("/menu")} className="flex items-center gap-1 text-sm text-[#A0A0A0] hover:text-white mb-4">
          <ChevronLeft size={16} /> Back to menu
        </button>

        <div className="flex items-end justify-between gap-3 flex-wrap mb-4">
          <div>
            <h1 className="text-xl font-black text-white">Tax groups</h1>
            <p className="text-xs text-[#A0A0A0] mt-1">Which GST rate each item is billed at. Food uses the statutory slab; add alcohol or other groups in Settings &rarr; Billing &amp; Tax.</p>
          </div>
          <button onClick={save} disabled={saving || !dirty}
            className="flex items-center gap-1.5 bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-40 text-white text-sm font-bold rounded-lg px-4 py-2.5">
            <Save size={15} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>

        <div className="relative mb-3">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A0A0A0]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search items or a category…"
            className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder:text-[#6B6B6B] focus:border-[#9D00FF] focus:outline-none" />
        </div>

        {taxGroups.length > 1 && (
          <div className="flex items-center gap-2 flex-wrap mb-3 text-xs text-[#A0A0A0]">
            <span>Set all {visible.length} shown to:</span>
            {taxGroups.map((g) => (
              <button key={g.group} type="button" onClick={() => setAllVisible(g.group)}
                className="px-2.5 py-1 rounded-lg border border-white/10 text-[#A0A0A0] hover:border-[#9D00FF] hover:text-white">
                {g.label || g.group}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="text-sm text-[#A0A0A0] py-10">Loading…</div>
        ) : (
          <div className="bg-[#1A1A1A] border border-white/10 rounded-xl overflow-hidden">
            <div className="grid grid-cols-[1fr_200px] gap-2 px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-[#A0A0A0] border-b border-white/10">
              <span>Item</span><span>Tax group</span>
            </div>
            <div className="max-h-[62vh] overflow-y-auto">
              {visible.map((it) => {
                const g = groups[it.id] || "food";
                const orphan = !groupKeys.includes(g);
                return (
                  <div key={it.id} className="grid grid-cols-[1fr_200px] gap-2 px-4 py-2 items-center border-b border-white/5">
                    <div className="min-w-0">
                      <div className="text-sm text-white truncate">{it.name}</div>
                      <div className="text-[11px] text-[#A0A0A0] truncate">{catName(it.category_id)}</div>
                    </div>
                    <select value={g} onChange={(e) => setGroups((p) => ({ ...p, [it.id]: e.target.value }))}
                      className={`w-full bg-[#0A0A0A] border rounded-lg px-3 py-2 text-sm text-white focus:outline-none ${orphan ? "border-red-500" : "border-white/10 focus:border-[#9D00FF]"}`}>
                      {taxGroups.map((tg) => <option key={tg.group} value={tg.group}>{(tg.label || tg.group)} · {Number(tg.rate) || 0}%</option>)}
                      {orphan && <option value={g}>⚠ {g} — removed</option>}
                    </select>
                  </div>
                );
              })}
              {visible.length === 0 && <div className="text-sm text-[#555] px-4 py-8">No items match.</div>}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
